"use strict";

/**
 * DGF-4945: the mini bag panel doesn't scroll; only the product list does, between a fixed top (title, delivery box)
 * and a fixed footer (subtotal, saving, total, Secure Checkout, the express wallets). The panel's height is capped
 * at the space left between the top of the panel and the bottom of the window. The header is sticky and slides up on
 * scroll, so that space is measured each time the bag opens or the window is resized rather than fixed in CSS;
 * _miniCart.scss reads the result as --minicart-max-height and falls back to a CSS estimate before this has run.
 *
 * When the window is short, the top and footer would leave the product list no room. Rather than push anything
 * out of sight, the panel drops its extras (the savings bar, the "or" divider and free returns) and then tightens, in steps
 * (COMPACT_STEPS, styled in _miniCart.scss), measuring after each and stopping at the first that leaves the list at
 * least MIN_LIST_HEIGHT.
 */

// The popover's ::before spacer above the panel (_miniCart.scss) and the gap kept below it
var SPACER_HEIGHT = 28;
var BOTTOM_GAP = 16;
// Never shorter than this, so a very short window still shows the panel rather than a sliver of it
var MIN_HEIGHT = 240;
// The least of the product list worth keeping in view: a product card with room to spare. 120 left the list too
// cramped once the express wallets had mounted and grown the footer.
var MIN_LIST_HEIGHT = 160;
// Cumulative: step 2 keeps step 1's class, and so on
var COMPACT_STEPS = ["minicart--compact-1", "minicart--compact-2", "minicart--compact-3", "minicart--compact-4"];

var resizeBound = false;
var footerObserver = null;
var observedFooter = null;

/**
 * @param {number} popoverTop - the popover's top edge, from the top of the viewport
 * @param {number} viewportHeight - window.innerHeight
 * @returns {number} the height the panel may take, in pixels
 */
function getMaxHeight(popoverTop, viewportHeight) {
    return Math.max(Math.floor(viewportHeight - popoverTop - SPACER_HEIGHT - BOTTOM_GAP), MIN_HEIGHT);
}

/**
 * @param {Object} panel - the mini bag panel (.cart)
 * @param {Object} list - the scrolling product list (.product-summary)
 * @param {number} maxHeight - the height the panel may take
 * @returns {boolean} whether the top and footer leave the list less than it needs
 */
function needsMoreRoom(panel, list, maxHeight) {
    // scrollHeight is the panel's full content height even while it's capped; the list's own box is what it got
    var chromeHeight = panel.scrollHeight - list.offsetHeight;
    var listHeight = Math.min(list.scrollHeight, MIN_LIST_HEIGHT);

    return chromeHeight + listHeight > maxHeight;
}

/**
 * Starts from the full layout and adds compact steps one at a time until the list has room, or the steps run out.
 * @param {Object} panel - the mini bag panel (.cart)
 * @param {Object} list - the scrolling product list (.product-summary)
 * @param {number} maxHeight - the height the panel may take
 * @returns {number} how many steps were applied, 0 to COMPACT_STEPS.length
 */
function applyCompactSteps(panel, list, maxHeight) {
    var applied = 0;

    COMPACT_STEPS.forEach(function (step) {
        panel.classList.remove(step);
    });

    while (applied < COMPACT_STEPS.length && needsMoreRoom(panel, list, maxHeight)) {
        panel.classList.add(COMPACT_STEPS[applied]);
        applied++;
    }

    return applied;
}

/**
 * @returns {Object|null} the open popover that is actually rendered, or null when there is none. A popover can keep
 * .show while not being rendered: CSS hides it below md (e.g. when DevTools docked to the side narrows the window),
 * and the page may hold more than one .minicart .popover. A hidden one measures 0 high at the top of the page, and
 * fitting it would clear the steps and set a height for a bag nobody can see.
 */
function findOpenPopover() {
    var popovers = document.querySelectorAll(".minicart .popover.show");

    for (var i = 0; i < popovers.length; i++) {
        if (popovers[i].getClientRects().length > 0) {
            return popovers[i];
        }
    }

    return null;
}

/**
 * Sets --minicart-max-height on the open popover and tightens the panel if it needs to. Does nothing while the bag
 * is closed.
 */
function fitMiniCartToViewport() {
    var popover = findOpenPopover();

    if (!resizeBound) {
        window.addEventListener("resize", fitMiniCartToViewport);
        resizeBound = true;
    }

    if (!popover) {
        return;
    }

    var maxHeight = getMaxHeight(popover.getBoundingClientRect().top, window.innerHeight);
    popover.style.setProperty("--minicart-max-height", maxHeight + "px");

    var panel = popover.querySelector(".cart");
    var list = popover.querySelector(".product-summary");
    if (panel && list) {
        applyCompactSteps(panel, list, maxHeight);
    }

    watchFooter(popover.querySelector(".minicart-footer"));
}

/**
 * The express wallets mount after the bag has loaded and the footer grows when they do, so the fit is redone
 * whenever the footer changes size. Each load renders a new footer, so the observer moves to it. Fitting again
 * settles: it rebuilds the same steps from scratch, so the footer ends at the size it was observed at.
 * @param {Object|null} footer - the open bag's .minicart-footer
 */
function watchFooter(footer) {
    if (!footer || footer === observedFooter || typeof window.ResizeObserver !== "function") {
        return;
    }

    if (!footerObserver) {
        footerObserver = new window.ResizeObserver(function () {
            fitMiniCartToViewport();
        });
    }

    if (observedFooter) {
        footerObserver.unobserve(observedFooter);
    }
    footerObserver.observe(footer);
    observedFooter = footer;
}

module.exports = fitMiniCartToViewport;
module.exports.getMaxHeight = getMaxHeight;
module.exports.applyCompactSteps = applyCompactSteps;
module.exports.COMPACT_STEPS = COMPACT_STEPS;
